import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Heart, ShoppingBag, Menu, X, ChevronDown, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { useCart } from '../../context/CartContext';
import { getCategories } from '../../services/categories';
import { searchProducts } from '../../services/products';
import { getSiteSettings } from '../../services/settings';
import { parsePromoLines } from '../../lib/promoBanner';
import PromoBanner from './PromoBanner';
import Logo from './Logo';

const Header = () => {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [showPromo, setShowPromo] = useState(true);
  const [promoLines, setPromoLines] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const { cartCount, wishlistItems } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const cats = await getCategories();
        const flattenCategories = (items) => {
          const result = [];
          const walk = (list) => {
            list.forEach((item) => {
              result.push({
                ...item,
                id: String(item.id),
                parentId: item.parentId ? String(item.parentId) : null,
              });
              if (item.children?.length) {
                walk(item.children);
              }
            });
          };
          walk(items);
          return result;
        };
        const flat = flattenCategories(cats);
        setAllCategories(flat);
        // Filter top-level categories (no parentId) for navigation
        const topLevelCategories = flat.filter((cat) => !cat.parentId);
        setCategories(topLevelCategories);
      } catch (error) {
        console.error('Error fetching categories:', error);
        setCategories([]);
        setAllCategories([]);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchPromoText = async () => {
      try {
        const data = await getSiteSettings();
        const lines = parsePromoLines(data.announcement);
        if (lines.length === 0) {
          setPromoLines([]);
          setShowPromo(false);
          return;
        }
        setPromoLines(lines);
        setShowPromo(true);
      } catch (error) {
        console.error('Error fetching promo banner text:', error);
        setPromoLines(parsePromoLines(null));
        setShowPromo(true);
      }
    };
    fetchPromoText();
  }, []);

  // Get subcategories for a category
  const getSubcategories = (categoryId) => {
    return allCategories.filter((cat) => cat.parentId === categoryId);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const executeSearch = (value) => {
    const query = value.trim();
    if (!query) return;
    navigate(`/search?q=${encodeURIComponent(query)}`);
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    executeSearch(searchQuery);
  };

  useEffect(() => {
    if (!searchOpen || !searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    const query = searchQuery.trim();
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    const timeout = setTimeout(async () => {
      try {
        setIsSearching(true);
        const results = await searchProducts(query);
        setSearchResults(results.slice(0, 6));
      } catch (error) {
        console.error('Error searching products:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [searchOpen, searchQuery]);

  return (
    <>
      {/* Promo Banner */}
      {showPromo && promoLines.length > 0 && (
        <PromoBanner
          lines={promoLines}
          onClose={() => setShowPromo(false)}
          closeLabel={t('common.close')}
        />
      )}

      {/* Main Header */}
      <header
        className={`sticky top-0 z-50 bg-white transition-shadow duration-300 ${
          isScrolled ? 'shadow-md' : 'shadow-sm'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Mobile Menu Button */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon">
                  <Menu size={24} />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[300px] sm:w-[340px] p-0 bg-white [&>button]:hidden">
                <div className="flex flex-col h-full">
                  <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                    <div onClick={() => setMobileMenuOpen(false)}>
                      <Logo />
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setMobileMenuOpen(false)}
                      aria-label="Menüyü kapat"
                    >
                      <X size={24} className="text-black" />
                    </Button>
                  </div>
                  <nav className="flex-1 overflow-y-auto py-4">
                    {categories.map((category) => {
                      const subcategories = getSubcategories(category.id);
                      const hasSubs = subcategories.length > 0;
                      return (
                        <Collapsible key={category.id} className="border-b border-gray-100">
                          <div className="flex items-center">
                            {hasSubs ? (
                              <CollapsibleTrigger className="flex-1 flex items-center justify-between py-3 px-4 font-medium text-base text-black hover:bg-gray-50 transition-colors text-left group">
                                <span>{category.name}</span>
                                <ChevronRight size={20} className="flex-shrink-0 transition-transform duration-300 group-data-[state=open]:rotate-90" />
                              </CollapsibleTrigger>
                            ) : (
                              <Link
                                to={`/category/${category.id}`}
                                className="flex-1 py-3 px-4 font-medium text-base text-black hover:bg-gray-50 transition-colors"
                                onClick={() => setMobileMenuOpen(false)}
                              >
                                {category.name}
                              </Link>
                            )}
                          </div>
                          {hasSubs && (
                            <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                              <div className="pl-6 pr-4 pb-3 space-y-1">
                                <Link
                                  to={`/category/${category.id}`}
                                  className="block py-2 text-sm font-medium text-gray-900 hover:text-black hover:bg-gray-50 px-2 -mx-2 rounded transition-colors"
                                  onClick={() => setMobileMenuOpen(false)}
                                >
                                  {t('nav.shopAll')} {category.name}
                                </Link>
                                {subcategories.map((sub) => (
                                  <Link
                                    key={sub.id}
                                    to={`/category/${category.id}/${sub.id}`}
                                    className="block py-2 text-sm text-gray-700 hover:text-black hover:bg-gray-50 px-2 -mx-2 rounded transition-colors"
                                    onClick={() => setMobileMenuOpen(false)}
                                  >
                                    {sub.name}
                                  </Link>
                                ))}
                              </div>
                            </CollapsibleContent>
                          )}
                        </Collapsible>
                      );
                    })}
                    <Link
                      to="/order-tracking"
                      className="block py-3 px-4 font-medium text-base text-black hover:bg-gray-50 transition-colors border-b border-gray-100"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Sipariş Takibi
                    </Link>
                    <Link
                      to="/sale"
                      className="block py-3 px-4 font-medium text-base text-red-600 hover:bg-gray-50 hover:text-red-700 transition-colors border-b border-gray-100"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {t('common.sale')}
                    </Link>
                    <Link
                      to="/new-arrivals"
                      className="block py-3 px-4 font-medium text-base text-black hover:bg-gray-50 transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {t('common.newArrivals')}
                    </Link>
                  </nav>
                </div>
              </SheetContent>
            </Sheet>

            {/* Logo */}
            <Logo />

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              {categories.map((category) => {
                const subcategories = getSubcategories(category.id);
                return (
                  <DropdownMenu key={category.id}>
                    <DropdownMenuTrigger className="flex items-center space-x-1 text-sm font-medium hover:text-blue-600 transition-colors outline-none">
                      <span>{category.name}</span>
                      <ChevronDown size={14} />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-48">
                      <DropdownMenuItem asChild>
                        <Link
                          to={`/category/${category.id}`}
                          className="font-semibold"
                        >
                          {t('nav.shopAll')} {category.name}
                        </Link>
                      </DropdownMenuItem>
                      {subcategories.length > 0 && subcategories.map((sub) => (
                        <DropdownMenuItem key={sub.id} asChild>
                          <Link to={`/category/${category.id}/${sub.id}`}>
                            {sub.name}
                          </Link>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              })}
              <Link
                to="/order-tracking"
                className="text-sm font-medium hover:text-blue-600 transition-colors"
              >
                Sipariş Takibi
              </Link>
              <Link
                to="/sale"
                className="text-sm font-medium text-red-600 hover:text-red-700 transition-colors"
              >
                {t('common.sale')}
              </Link>
              <Link
                to="/new-arrivals"
                className="text-sm font-medium hover:text-blue-600 transition-colors"
              >
                {t('common.newArrivals')}
              </Link>
            </nav>

            {/* Right Actions */}
            <div className="flex items-center space-x-2 sm:space-x-4">
              {/* Search */}
              <div className="relative">
                {searchOpen ? (
                  <form onSubmit={handleSearch} className="flex items-center">
                    <Input
                      type="text"
                      placeholder={t('common.search')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-32 sm:w-48 h-9 text-sm"
                      autoFocus
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9"
                      onClick={() => setSearchOpen(false)}
                    >
                      <X size={18} />
                    </Button>
                  </form>
                ) : (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setSearchOpen(true)}
                    aria-label={t('common.search')}
                  >
                    <Search size={20} />
                  </Button>
                )}
                {searchOpen && searchQuery.trim().length >= 2 && (
                  <div className="absolute right-0 mt-2 w-80 bg-white border rounded-lg shadow-lg z-50">
                    {isSearching ? (
                      <div className="p-4 text-sm text-gray-500">Aranıyor...</div>
                    ) : searchResults.length > 0 ? (
                      <div className="max-h-80 overflow-y-auto">
                        {searchResults.map((product) => (
                          <Link
                            key={product.id}
                            to={`/product/${product.id}`}
                            onClick={() => {
                              setSearchOpen(false);
                              setSearchQuery('');
                              setSearchResults([]);
                            }}
                            className="flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors"
                          >
                            <img
                              src={product.images?.[0] || '/placeholder.jpg'}
                              alt={product.name}
                              className="w-12 h-14 object-cover rounded"
                            />
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {product.name}
                              </p>
                              <p className="text-xs text-gray-500 line-clamp-1">
                                {product.description || 'Ürün açıklaması yok'}
                              </p>
                            </div>
                          </Link>
                        ))}
                        <button
                          type="button"
                          onClick={() => executeSearch(searchQuery)}
                          className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50"
                        >
                          Tüm sonuçları gör
                        </button>
                      </div>
                    ) : (
                      <div className="p-4 text-sm text-gray-500">Sonuç bulunamadı</div>
                    )}
                  </div>
                )}
              </div>

              {/* Wishlist */}
              <Link to="/wishlist" className="relative">
                <Button variant="ghost" size="icon" aria-label={t('common.wishlist')}>
                  <Heart size={20} />
                </Button>
                {wishlistItems.length > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-red-500">
                    {wishlistItems.length}
                  </Badge>
                )}
              </Link>

              {/* Cart */}
              <Link to="/cart" className="relative">
                <Button variant="ghost" size="icon" aria-label={t('common.cart')}>
                  <ShoppingBag size={20} />
                </Button>
                {cartCount > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-[#1a1a2e]">
                    {cartCount}
                  </Badge>
                )}
              </Link>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
